import { Col, Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import checkLeiApi from 'services/akcionar/emise/checkLei';
import { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';
import { IRozcestnikData } from 'services/akcionar/emise/rozcestnikISIN';
import { isinGetNaceList } from 'services/akcionar/emise/zadostISIN';
import { IcisMeny } from 'services/cisMeny';
import { IcisStaty } from 'services/cisStaty';

import { EAnoNe } from 'enums/anoNe';
import { EEmitentZadaOpridISIN } from 'enums/isin/emitentZadaOpridISIN';
import { naceRegExp } from 'enums/regExp';

import anoNe from 'data/codebooks/anoNeSelect';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import dicValidation from 'utils/validations/dicValidation';
import dveDesetinneMistaValidation from 'utils/validations/dveDesetinneMistaValidation';
import emailValidation from 'utils/validations/emailValidation';
import leiValidation from 'utils/validations/leiValidation';
import phoneValidation from 'utils/validations/phoneValidation';
import { procentaValidation } from 'utils/validations/procentaValidation';

import { IRozcestnikInput } from '../novyISIN/IdentifikaceEmitentaNovy';

interface IIdentifikaceEmitentaZmena {
    cisStaty: IcisStaty[];
    cisMeny: IcisMeny[];
    prevStep: () => void;
    rozcestnikData: IRozcestnikData['spolecnost'] | undefined;
    rozcestnikInput: IRozcestnikInput | undefined;
    setIdentifikacePutData: React.Dispatch<React.SetStateAction<IIdentifikaceEmitenta | undefined>>;
    identifikacePutData: IIdentifikaceEmitenta | undefined;
    rozpracovanaZadost: boolean;
}

const IdentifikaceEmitentaZmena: React.FC<IIdentifikaceEmitentaZmena> = ({
    cisStaty,
    cisMeny,
    prevStep,
    rozcestnikData,
    rozcestnikInput,
    setIdentifikacePutData,
    identifikacePutData
}) => {
    const [form] = Form.useForm();
    const { aktivniFirma, isin, profily } = useContext(Context);
    const setForceRerender = useState<boolean>(false)[1];
    const [leiError, setLeiError] = useState<string>();
    const [naceData, setNaceData] = useState<{ labelcz: string; value: string }[]>();
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);

    useEffect(() => {
        form.setFieldsValue({
            stat: aktivniFirma?.stat?.toUpperCase()
        });
    }, [aktivniFirma?.stat, form]);

    useEffect(() => {
        ((identifikacePutData && typeof identifikacePutData.madic === 'boolean' && identifikacePutData.madic) ||
            (isin && typeof isin.madic === 'boolean' && isin.madic)) &&
            form.setFieldsValue({
                madic: EAnoNe.ANO
            });
        isinGetNaceList().then(
            resp =>
                resp.nacelist.length &&
                setNaceData(
                    resp.nacelist.map(e => {
                        return {
                            labelcz: e.nacepopis,
                            value: e.nace
                        };
                    })
                )
        );
        setForceRerender(val => !val);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        const finishData = {
            ...rozcestnikInput,
            ...identifikacePutData,
            ...value,
            ...(value.bankspojeni && {
                bankspojeni: value.bankspojeni.split(' ').join('')
            }),
            madic: value.madic === EAnoNe.ANO,
            kost: value.stat,
            nacepopis:
                naceData && rozcestnikInput?.podobacp !== EEmitentZadaOpridISIN.LISTINNY
                    ? naceData?.filter(e => value.nace === e.value)[0].labelcz
                    : value.nacepopis
        };
        if (value.madic === EAnoNe.NE) {
            delete finishData.dic;
        }
        setIdentifikacePutData(finishData);
    };

    const onSelectChange = (name?: string) => {
        if (name === 'madic') {
            setForceRerender(val => !val);
        }
    };

    return (
        <>
            <FormWrapper outForm={form} onFinish={onFinish}>
                <Title level={5} text="ISIN_IDENTIFIKACE_IDENTIFIKACE_EMITENTA" dividerTitle={true} />
                <InputComponent
                    disabled={true}
                    label="IDENTIFIKACNI_CISLO"
                    name="idmb"
                    type="text"
                    maxLength={10}
                    initialValue={rozcestnikData?.idmb || identifikacePutData?.idmb || isin?.idmb}
                />
                <InputComponent
                    disabled={!!rozcestnikData?.nazev || !!identifikacePutData?.nazev || !!isin?.nazev}
                    label="ISIN_IDENTIFIKACE_FIRMA"
                    name="nazev"
                    initialValue={rozcestnikData?.nazev || identifikacePutData?.nazev || isin?.nazev}
                    type="text"
                    maxLength={500}
                />
                <LoadingWrapper loading={leiCheckLoading} loadingText="NACITANI_DAT">
                    <InputComponent
                        label="LEI_CELE"
                        name="lei"
                        initialValue={rozcestnikData?.lei || identifikacePutData?.lei || isin?.lei}
                        extramessage={leiError}
                        maxLength={20}
                        type="text"
                        rules={[
                            ...leiValidation(async (value: string) => {
                                setLeiCheckLoading(true);
                                const resp = value ? await checkLeiApi(value).catch(() => null) : null;
                                setLeiCheckLoading(false);

                                if (
                                    (form.getFieldValue('stat') === 'CZ' &&
                                        resp?.ic !==
                                            (rozcestnikData?.idmb || identifikacePutData?.idmb || isin?.idmb)) ||
                                    (form.getFieldValue('stat') !== 'CZ' && form.getFieldValue('stat') !== resp?.stat)
                                ) {
                                    setLeiError('FORMULARE_LEI_VEDENO_PRO_JINOU_SPOL');
                                    return Promise.reject();
                                } else if (resp?.stav !== 'ISSUED' && resp?.stav !== 'PENDING_TRANSFER') {
                                    setLeiError(
                                        resp?.stav === 'LAPSED' ? 'FORMULARE_LEI_LAPSED_FO' : 'FORMULARE_LEI_ZRUSENE'
                                    );
                                    return Promise.reject();
                                } else if (!resp && value) {
                                    setLeiError('LEI_CHECK_ERROR');
                                    return Promise.reject();
                                }
                                setLeiError('');
                                return Promise.resolve();
                            })
                        ]}
                    />{' '}
                </LoadingWrapper>
                <InputComponent
                    label="ISIN_IDENTIFIKACE_PRAVNI_FORMA"
                    name="pravniforma"
                    initialValue={rozcestnikData?.pravniforma || identifikacePutData?.pravniforma || isin?.pravniforma}
                    maxLength={500}
                    type="text"
                />

                <Title level={5} text="ISIN_IDENTIFIKACE_ADRESA_SIDLA" dividerTitle={true} />
                <InputComponent
                    label="ULICE_CP"
                    name="adma"
                    initialValue={rozcestnikData?.adma || identifikacePutData?.adma || isin?.adma}
                    type="text"
                    maxLength={50}
                />
                <InputComponent
                    label="MESTO"
                    name="mema"
                    initialValue={rozcestnikData?.mema || identifikacePutData?.mema || isin?.mema}
                    type="text"
                    maxLength={50}
                />
                <InputComponent
                    label="PSC"
                    name="pscm"
                    initialValue={rozcestnikData?.pscm || identifikacePutData?.pscm || isin?.pscm}
                    maxLength={10}
                    type="text"
                />
                <SelectComponent
                    disabled={true}
                    name="stat"
                    labelParagraph="STAT_SIDLA"
                    isFormItem={true}
                    defaultValue={rozcestnikData?.stat || identifikacePutData?.stat || isin?.stat}
                    data={cisStaty}
                />

                <>
                    <Title level={5} text="DOPLNUJICI_INFORMACE" dividerTitle={true} />
                    <InputNumberComponent
                        name="kapital"
                        label="ISIN_IDENTIFIKACE_KAPITAL"
                        rules={dveDesetinneMistaValidation()}
                        initialValue={identifikacePutData?.kapital || isin?.kapital}
                        maxLength={19}
                        formatter={formatInputNumberThousand}
                    />

                    <SelectComponent
                        name="mena"
                        labelParagraph="ISIN_IDENTIFIKACE_MENA"
                        isFormItem={true}
                        defaultValue={identifikacePutData?.mena || isin?.mena}
                        data={cisMeny}
                    />
                    <InputNumberComponent
                        label="ISIN_IDENTIFIKACE_SPLACENO"
                        name="splaceno"
                        rules={procentaValidation()}
                        initialValue={identifikacePutData?.splaceno || isin?.splaceno || undefined}
                        maxLength={5}
                        formatter={formatInputNumberThousand}
                    />

                    <SelectComponent
                        name="madic"
                        labelParagraph="ISIN_IDENTIFIKACE_PLATCE"
                        isFormItem={true}
                        data={anoNe}
                        onChange={() => onSelectChange('madic')}
                        defaultValue={
                            identifikacePutData && typeof identifikacePutData.madic === 'boolean'
                                ? identifikacePutData.madic
                                    ? EAnoNe.ANO
                                    : EAnoNe.NE
                                : isin && typeof isin.madic === 'boolean'
                                  ? isin.madic
                                      ? EAnoNe.ANO
                                      : EAnoNe.NE
                                  : undefined
                        }
                    />
                    {(form.getFieldValue('madic') === EAnoNe.ANO || isin?.dic) && (
                        <InputComponent
                            initialValue={isin?.dic}
                            label="ISIN_IDENTIFIKACE_DIC"
                            name="dic"
                            maxLength={14}
                            rules={dicValidation()}
                        />
                    )}

                    <SelectComponent
                        name="stat"
                        labelParagraph="ZEME_SIDLA_VLASTNIKA_EMITENTA"
                        isFormItem={true}
                        data={cisStaty}
                        defaultValue={identifikacePutData?.stat || isin?.stat}
                    />

                    {naceData ? (
                        <SelectComponent
                            isFormItem={true}
                            name="nace"
                            defaultValue={identifikacePutData?.nace || isin?.nace}
                            labelParagraph="NACE"
                            data={naceData.map(e => {
                                return {
                                    value: e.value,
                                    labelcz: e.value + ' - ' + e.labelcz
                                };
                            })}
                        />
                    ) : (
                        <InputComponent
                            label="CZ_NACE"
                            infoTooltipText="FORMULARE_NACE_TOOLTIP"
                            maxLength={17}
                            name="nacepopis"
                            initialValue={identifikacePutData?.nacepopis || isin?.nacepopis}
                            rules={[
                                () => ({
                                    validator(_, value: string) {
                                        if (!value || naceRegExp.test(value)) return Promise.resolve();
                                        return Promise.reject(<TranslateText>NEVALIDNI_NACE</TranslateText>);
                                    }
                                })
                            ]}
                        />
                    )}
                </>

                <InputComponent
                    label="ISIN_IDENTIFIKACE_EMAIL_FAKTURY"
                    name="email"
                    rules={emailValidation()}
                    initialValue={identifikacePutData?.email || isin?.email}
                />
                <InputComponent
                    label="ISIN_IDENTIFIKACE_EMAIL_OSOBY"
                    name="emailkont"
                    maxLength={128}
                    initialValue={profily?.email || identifikacePutData?.emailkont || isin?.emailkont}
                    rules={emailValidation()}
                    disabled={!!profily?.email}
                />
                <InputComponent
                    label="ISIN_IDENTIFIKACE_TELEFON"
                    name="telefon"
                    maxLength={17}
                    initialValue={profily?.telefon1 || identifikacePutData?.telefon || isin?.telefon}
                    rules={phoneValidation()}
                    disabled={!!profily?.telefon1}
                />
                <Row className="button-row">
                    {!isin && (
                        <Col>
                            <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} type="default" id="zpetBtn" />
                        </Col>
                    )}
                    <Col>
                        <ButtonComponent buttontext="POKRACOVAT" onClick={() => form.submit()} id="pokracovatBtn" />
                    </Col>
                </Row>
            </FormWrapper>
        </>
    );
};

export default IdentifikaceEmitentaZmena;
